/**
 * Who builds it.
 *
 * The hardest page on this site to write honestly, because it is the one with no
 * acceptance matrix behind it. Everything on the product and security pages is
 * checked against the code — `routing.test.ts` reads a Go file and fails on
 * drift, `fixtures.test.ts` greps for leaked addresses — and a company page has
 * no such gate. So it says what is verifiable and stops: the legal name, the
 * address, the two ways to reach a person, and what the product is for. No
 * founding year nobody checked, no customer count, no "leading provider".
 *
 * The three principles are not values in the poster sense. They are the three
 * rules the software itself is built on — the ones a buyer inherits by running
 * it — and each is a real constraint in the codebase rather than a sentiment:
 * association is recorded at execution time and never owned, unmeasured capacity
 * is never rendered as a number, and completion is unreachable without evidence.
 * A reader can check all three against the product page.
 */
import { Reveal, Stagger } from '../kit'
import { useLocale, useT } from '../i18n/context'
import { COMPANY } from '../data/links'
import { pathFor } from '../routes'
import { PageHeader } from '../sections/PageHeader'
import { Closing } from '../sections/Closing'
import type { MessageKey } from '../i18n'

const PRINCIPLES: { key: string; title: MessageKey; body: MessageKey }[] = [
  { key: 'owned', title: 'company.p1.title', body: 'company.p1.body' },
  { key: 'unknown', title: 'company.p2.title', body: 'company.p2.body' },
  { key: 'evidence', title: 'company.p3.title', body: 'company.p3.body' },
]

export function CompanyPage() {
  const t = useT()
  const locale = useLocale()

  return (
    <>
      <PageHeader
        eyebrow="companyPage.eyebrow"
        title="companyPage.title"
        sub="companyPage.sub"
        toc={[]}
      />

      <section className="kl-section kl-section--sunken" aria-labelledby="who-title">
        <div className="kl-inner">
          <Reveal className="kl-head">
            {/* The legal name comes from the company record rather than from
                the catalogue: it is the same string in both languages, and a key
                whose two translations are identical is a key that will be
                "translated" wrongly the first time somebody tidies the file. */}
            <h2 id="who-title" className="kl-title">
              {COMPANY.legalName}
            </h2>
          </Reveal>
          <div className="kl-section__body">
            <p className="kl-body">{t('company.who.body.1')}</p>
            <p className="kl-pull">{t('company.who.pull')}</p>
            <p className="kl-body">{t('company.who.body.2')}</p>
          </div>

          {/* The registered details, as a description list rather than prose:
              this is the block a reader copies into a procurement form, and a
              sentence is a worse place to copy a line of an address from. */}
          <dl className="kl-factgrid">
            <div className="kl-fact">
              <dt className="kl-fact__key">{t('company.fact.legal')}</dt>
              <dd className="kl-fact__val">{COMPANY.legalName}</dd>
            </div>
            <div className="kl-fact">
              <dt className="kl-fact__key">{t('company.fact.address')}</dt>
              <dd className="kl-fact__val">
                {COMPANY.address}
                <br />
                {COMPANY.district} · {COMPANY.country}
              </dd>
            </div>
            <div className="kl-fact">
              <dt className="kl-fact__key">{t('company.fact.email')}</dt>
              <dd className="kl-fact__val">
                <a className="kl-textlink" href={`mailto:${COMPANY.email}`}>
                  {COMPANY.email}
                </a>
              </dd>
            </div>
            <div className="kl-fact">
              <dt className="kl-fact__key">{t('company.fact.phone')}</dt>
              <dd className="kl-fact__val">
                <a className="kl-textlink k-mono" href={COMPANY.phoneHref}>
                  {COMPANY.phone}
                </a>
              </dd>
            </div>
          </dl>
        </div>
      </section>

      <section className="kl-section" aria-labelledby="principles-title">
        <div className="kl-inner">
          <Reveal className="kl-head">
            <p className="k-eyebrow">
              <span className="k-label">{t('company.principles.eyebrow')}</span>
            </p>
            <h2 id="principles-title" className="kl-title">
              {t('company.principles.title')}
            </h2>
            <p className="kl-sub">{t('company.principles.sub')}</p>
          </Reveal>

          <Stagger className="kl-principles" as="ol" childAs="li" step={0.07}>
            {PRINCIPLES.map((p, i) => (
              <div key={p.key} className="kl-principle">
                <span className="kl-principle__num k-mono" aria-hidden="true">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="kl-principle__title">{t(p.title)}</h3>
                <p className="kl-principle__body">{t(p.body)}</p>
              </div>
            ))}
          </Stagger>

          <p className="kl-showcase__more">
            <a className="kl-textlink" href={pathFor(locale, 'product')}>
              {t('company.principles.link')}
            </a>
          </p>
        </div>
      </section>

      <Closing />
    </>
  )
}
